import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { speechCode } from '../lib/languages';
import { paragraphs as splitParagraphs } from '../lib/reading';
import { translate, type TranslationSource } from '../lib/translate';

// ─────────────────────────────────────────────────────────────────────────────
// Text you can tap or highlight to translate.
//
//   tap a word          → that word
//   drag across words   → exactly what is highlighted
//
// Lookups go through the classroom-translate function: the app's own saved
// translation when there is one, Google Translate otherwise — never a new
// Claude call. Desktop selections are handled on pointerup; on phones and
// tablets a selection is made with the OS handles after the finger lifts, so
// `selectionchange` (debounced) catches those.
//
// Listening uses the browser's own speech synthesis — free, offline, and good
// enough to hear how a word is said. No narration credits.
// ─────────────────────────────────────────────────────────────────────────────

type Props = {
  text: string;
  source: string;
  target: string;
  /** Called once per successful lookup. */
  onLookup?: (text: string, translation: string) => void;
  /** Students only: save what is in the popup as a flashcard. */
  onSave?: (term: string, translation: string) => Promise<void>;
  /** termKey()s already in the student's deck, to show "Saved" instead. */
  savedKeys?: Set<string>;
  className?: string;
};

type Popover = {
  text: string;
  top: number;
  left: number;
  translation?: string;
  from?: TranslationSource;
  error?: string;
};

const WORD_RE = /[\p{L}\p{M}\p{N}]+(?:['’-][\p{L}\p{M}\p{N}]+)*/gu;
const POPOVER_HALF = 150;

type Token = { word: boolean; text: string };

/**
 * Words and the bits between them. The browser's own word segmenter knows
 * every language — including ones written without spaces, and Arabic with
 * its attached particles — so it is used whenever it exists; the regex is the
 * fallback for old browsers.
 */
function tokenize(line: string, locale = 'es'): Token[] {
  const Seg = (Intl as unknown as { Segmenter?: new (l: string, o: { granularity: 'word' }) => { segment(s: string): Iterable<{ segment: string; isWordLike?: boolean }> } }).Segmenter;
  if (Seg) {
    const out: Token[] = [];
    for (const part of new Seg(locale, { granularity: 'word' }).segment(line)) {
      const word = !!part.isWordLike;
      const prev = out[out.length - 1];
      if (!word && prev && !prev.word) prev.text += part.segment;
      else out.push({ word, text: part.segment });
    }
    return out;
  }
  const out: Token[] = [];
  let last = 0;
  for (const m of line.matchAll(WORD_RE)) {
    const i = m.index ?? 0;
    if (i > last) out.push({ word: false, text: line.slice(last, i) });
    out.push({ word: true, text: m[0] });
    last = i + m[0].length;
  }
  if (last < line.length) out.push({ word: false, text: line.slice(last) });
  return out;
}

export function speak(text: string, lang: string) {
  if (!('speechSynthesis' in window)) return;
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.lang = speechCode(lang);
  u.rate = 0.9;
  window.speechSynthesis.speak(u);
}

export default function TranslatableText({ text, source, target, onLookup, onSave, savedKeys, className }: Props) {
  const [saving, setSaving] = useState<'idle' | 'busy' | string>('idle');
  const boxRef = useRef<HTMLDivElement>(null);
  const pointerDown = useRef(false);
  const debounce = useRef<number | undefined>(undefined);
  const [pop, setPop] = useState<Popover | null>(null);
  const [looked, setLooked] = useState<Set<string>>(new Set());
  const popText = useRef<string | null>(null);

  const paragraphs = useMemo(
    () => splitParagraphs(text).map(p => p.split('\n').map(l => tokenize(l, source))),
    [text, source],
  );

  const open = useCallback(async (raw: string, rect: DOMRect) => {
    const box = boxRef.current;
    if (!box) return;
    const clean = raw.replace(/\s+/g, ' ').trim();
    if (!clean || !/[\p{L}\p{N}]/u.test(clean)) return;
    if (clean.length > 400) {
      popText.current = clean;
      const b = box.getBoundingClientRect();
      setPop({
        text: clean.slice(0, 60) + '…',
        top: rect.bottom - b.top + 8,
        left: Math.min(Math.max(rect.left + rect.width / 2 - b.left, POPOVER_HALF), b.width - POPOVER_HALF),
        error: 'That is a lot of text — highlight a sentence or two at a time.',
      });
      return;
    }
    if (popText.current === clean) return;
    popText.current = clean;

    const b = box.getBoundingClientRect();
    const left = Math.min(
      Math.max(rect.left + rect.width / 2 - b.left, Math.min(POPOVER_HALF, b.width / 2)),
      Math.max(b.width - POPOVER_HALF, b.width / 2),
    );
    const base: Popover = { text: clean, top: rect.bottom - b.top + 8, left };
    setPop(base);
    setSaving('idle');
    try {
      const result = await translate(clean, source, target);
      if (popText.current !== clean) return;
      setPop({ ...base, translation: result.text, from: result.from });
      if (!clean.includes(' ')) setLooked(prev => new Set(prev).add(clean.toLowerCase()));
      onLookup?.(clean, result.text);
    } catch (e) {
      if (popText.current !== clean) return;
      setPop({ ...base, error: e instanceof Error ? e.message : 'Translation failed.' });
    }
  }, [source, target, onLookup]);

  const close = useCallback(() => {
    popText.current = null;
    setPop(null);
    setSaving('idle');
  }, []);

  const save = async () => {
    if (!pop?.translation || !onSave) return;
    setSaving('busy');
    try {
      await onSave(pop.text, pop.translation);
      setSaving('idle');
    } catch (e) {
      setSaving(e instanceof Error ? e.message : 'Could not save.');
    }
  };

  /** A highlight inside this box, if there is one. */
  const readSelection = useCallback((): { text: string; rect: DOMRect } | null => {
    const sel = window.getSelection();
    const box = boxRef.current;
    if (!sel || sel.isCollapsed || sel.rangeCount === 0 || !box) return null;
    const range = sel.getRangeAt(0);
    if (!box.contains(range.commonAncestorContainer)) return null;
    const t = sel.toString();
    if (!t.trim()) return null;
    return { text: t, rect: range.getBoundingClientRect() };
  }, []);

  const onPointerUp = (e: React.PointerEvent) => {
    pointerDown.current = false;
    const s = readSelection();
    if (s) { void open(s.text, s.rect); return; }
    const el = (e.target as HTMLElement).closest<HTMLElement>('.w');
    if (el && boxRef.current?.contains(el)) {
      void open(el.textContent ?? '', el.getBoundingClientRect());
    } else if (!(e.target as HTMLElement).closest('.tt-pop')) {
      close();
    }
  };

  // Touch selections (OS handles) finish without a pointerup on our text.
  useEffect(() => {
    const onChange = () => {
      window.clearTimeout(debounce.current);
      if (pointerDown.current) return;
      debounce.current = window.setTimeout(() => {
        const s = readSelection();
        if (s) void open(s.text, s.rect);
      }, 700);
    };
    document.addEventListener('selectionchange', onChange);
    return () => {
      document.removeEventListener('selectionchange', onChange);
      window.clearTimeout(debounce.current);
    };
  }, [open, readSelection]);

  // Escape or a click anywhere else closes it.
  useEffect(() => {
    if (!pop) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') close(); };
    const onDown = (e: PointerEvent) => {
      if (!boxRef.current?.contains(e.target as Node)) close();
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onDown);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onDown);
    };
  }, [pop, close]);

  return (
    <div
      ref={boxRef}
      className={`tt ${className ?? ''}`}
      dir="auto"
      onPointerDown={e => {
        if (!(e.target as HTMLElement).closest('.tt-pop')) pointerDown.current = true;
      }}
      onPointerUp={onPointerUp}
    >
      {paragraphs.map((lines, pi) => (
        <p key={pi} data-p={pi}>
          {lines.map((tokens, li) => (
            <Fragment key={li}>
              {li > 0 && <br />}
              {tokens.map((t, ti) => (t.word
                ? <span key={ti} className={`w${looked.has(t.text.toLowerCase()) ? ' looked' : ''}`}>{t.text}</span>
                : <Fragment key={ti}>{t.text}</Fragment>))}
            </Fragment>
          ))}
        </p>
      ))}

      {pop && (
        <div className="tt-pop" style={{ top: pop.top, left: pop.left }} role="dialog" aria-live="polite">
          <div className="tt-src">
            <span>{pop.text}</span>
            <button
              className="icon" title="Listen" aria-label="Listen"
              onClick={() => speak(pop.text, source)}
            >🔊</button>
          </div>
          {pop.error
            ? <div className="tt-err">{pop.error}</div>
            : pop.translation === undefined
              ? <div className="tt-dst muted">Translating…</div>
              : (
                <>
                  <div className="tt-dst">{pop.translation}</div>
                  <div className="tt-foot">
                    <span className="tt-from">{pop.from === 'gibberfish' ? 'Gibberfish' : 'Google Translate'}</span>
                    {onSave && (savedKeys?.has(pop.text.replace(/\s+/g, ' ').trim().toLowerCase())
                      ? <span className="tt-saved">✓ In flashcards</span>
                      : (
                        <button className="small tt-save" onClick={save} disabled={saving === 'busy'}>
                          {saving === 'busy' ? 'Saving…' : '＋ Flashcard'}
                        </button>
                      ))}
                  </div>
                  {saving !== 'idle' && saving !== 'busy' && <div className="tt-err">{saving}</div>}
                </>
              )}
        </div>
      )}
    </div>
  );
}
